import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import { Button } from '../../components/ui/button';
import { SearchInput } from '../../components/SearchInput';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import Paginator from '../../components/Paginator';
import { useTrans } from '../../hooks/useTrans';

export default function ContactsIndex({ contacts, filters }) {
    const translate = useTrans();
    const [form, setForm] = useState({
        query: filters.query || '',
        order_column: filters.orderColumn || 'debtor_name',
        order_direction: filters.orderDirection || 'asc',
    });

    function handleSubmit(e) {
        e.preventDefault();
        router.get(window.location.pathname, form, { preserveState: true });
    }

    function handleSort(column) {
        const direction = form.order_column === column && form.order_direction === 'asc' ? 'desc' : 'asc';
        const newForm = { ...form, order_column: column, order_direction: direction };
        setForm(newForm);
        router.get(window.location.pathname, newForm, { preserveState: true });
    }

    function handleDelete(debtorId, contactId) {
        if (window.confirm(translate('Weet je zeker dat je dit contact wilt verwijderen?'))) {
            router.delete(`/contacts/verwijderen/${debtorId}/${contactId}`);
        }
    }

    function SortableHead({ column, children, className = '' }) {
        const isActive = form.order_column === column;
        const arrow = isActive ? (form.order_direction === 'asc' ? ' ↑' : ' ↓') : '';
        return (
            <TableHead
                className={`py-5 px-2.5 text-[#003545] font-normal text-sm cursor-pointer select-none hover:text-[#0e8e6a] ${className}`}
                onClick={() => handleSort(column)}
            >
                <span className="underline">{children}</span>{arrow}
            </TableHead>
        );
    }

    const hasContacts = contacts && contacts.data && contacts.data.length > 0;

    return (
        <div className="max-w-[1200px] mx-auto px-4">
            <div className="bg-white rounded-[3px] border border-[#bbb]">
                <div className="p-[30px] pb-0">
                    <form onSubmit={handleSubmit} className="flex flex-wrap items-center gap-2 mb-6">
                        <h3 className="text-2xl font-bold text-[#003545]">{translate('Contactpersonen')}</h3>
                        <div className="flex items-center gap-2 ml-auto">
                            <SearchInput
                                name="query"
                                value={form.query}
                                onChange={(e) => setForm({ ...form, query: e.target.value })}
                                onClear={() => {
                                    const newForm = { ...form, query: '' };
                                    setForm(newForm);
                                    router.get(window.location.pathname, newForm, { preserveState: true });
                                }}
                                showClear={!!filters.query}
                                placeholder={translate('Zoeken')}
                                className="w-56"
                            />
                            <Button variant="primary" type="submit">{translate('Zoeken')}</Button>
                            <Button variant="primary" asChild>
                                <Link href="/contacts/toevoegen">{translate('Contactpersoon aanmaken')}</Link>
                            </Button>
                        </div>
                    </form>
                </div>

                <div className="px-[30px] pb-[30px]">
                    <Table>
                        <TableHeader className="bg-[#ededed] [&_tr]:border-b-2 [&_tr]:border-[#d4d4d4]">
                            <TableRow>
                                <SortableHead column="contact_name">{translate('Contactpersoon')}</SortableHead>
                                <SortableHead column="debtor_name">{translate('Debiteur')}</SortableHead>
                                <TableHead className="py-5 px-2.5 text-[#003545] font-normal text-sm text-center">{translate('Acties')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {hasContacts ? (
                                contacts.data.map((contact, index) => (
                                    <TableRow key={contact.id} className={index % 2 === 1 ? 'bg-[#f5f5f5]' : ''}>
                                        <TableCell className="py-5 px-2.5">{contact.name}</TableCell>
                                        <TableCell className="py-5 px-2.5">{contact.debtor_name}</TableCell>
                                        <TableCell className="py-5 px-2.5 text-center">
                                            <div className="inline-flex items-center gap-4">
                                                <button
                                                    onClick={() => handleDelete(contact.debtor_id, contact.id)}
                                                    className="text-[#bbb] hover:text-[#dc3939] transition-all cursor-pointer"
                                                    title={translate('Verwijderen')}
                                                >
                                                    <svg className="h-5 w-5 fill-current" role="img">
                                                        <use href="#glyph-trash-can"></use>
                                                    </svg>
                                                </button>
                                                <Link
                                                    href={`/contacts/${contact.id}`}
                                                    className="text-[#bbb] hover:text-[#ffd400] transition-colors"
                                                    title={translate('Bewerken')}
                                                >
                                                    <svg className="h-5 w-5 fill-current" role="img">
                                                        <use href="#glyph-edit"></use>
                                                    </svg>
                                                </Link>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={3} className="py-5 px-2.5 text-center text-gray-500">
                                        {translate('Er zijn geen contactpersonen bekend')}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>

                    <Paginator links={contacts?.links} />
                </div>
            </div>
        </div>
    );
}
